/** Under the status chip for someone invited who hasn't signed in: why they are listed and can't be put on a run yet. */
export const INVITED_NOTE = 'Invited — hasn’t signed in yet';
/**
 * Who "Not for payroll" is offered for on the People list itself: someone Payroll has no record for
 * (their entity is only suggested), who has never been paid. Everyone in Users & Team is listed now,
 * so the ones payroll doesn't pay need a way off it that isn't waiting for an inbox item.
 */
export function canMarkNotForPayroll(p) {
    if (p.notForPayroll)
        return false;
    if (p.entity != null && !p.entity.suggested)
        return false;
    return !p.lastPaidAt && p.payStatus !== 'PAID' && p.payStatus !== 'FINAL_PAY_DONE';
}
export const PAY_STATUS_LABEL = {
    NOT_SET_UP: 'Not set up',
    READY: 'Ready to pay',
    PAID: 'Paid',
    FINAL_PAY_DONE: 'Final pay done',
};
/**
 * Where someone stands with Payroll, by the one rule every screen uses:
 *   - Final pay done — no longer active, has been paid, and nothing of theirs is still waiting to be paid;
 *   - Not set up — no pay rate, or no Wise / bank details to pay them with (and which);
 *   - Paid — has been paid at least once;
 *   - Ready to pay — set up, not paid yet.
 * `waitingItems` = their lines on any run that are not paid yet (a draft's included).
 */
export function payrollPayStatus(p) {
    const missing = [...(p.rateMissing ? ['pay rate'] : []), ...(p.hasPayDetails ? [] : ['Wise / bank details'])];
    if (p.status === 'INACTIVE' && p.paidItems > 0 && p.waitingItems === 0)
        return { status: 'FINAL_PAY_DONE', missing: [] };
    if (missing.length)
        return { status: 'NOT_SET_UP', missing };
    return { status: p.paidItems > 0 ? 'PAID' : 'READY', missing: [] };
}
/**
 * Spec §9 "Not for payroll": someone payroll said isn't theirs to pay — until they are set up after
 * all (a Payroll record exists), which is payroll changing its mind by doing it.
 */
export const isNotForPayroll = (p) => p.declined && !p.setUp;
/**
 * The People list under a filter. Someone marked "Not for payroll" is on no list but their own, so the
 * counts, the search and every other chip are about the people payroll actually pays.
 */
export function onPeopleList(p, filter) {
    return filter === 'not-for-payroll' ? p.notForPayroll === true : p.notForPayroll !== true;
}
/** Under the status chip: "Final pay planned", while it is still ahead of them. */
export function finalPayNote(p) {
    if (!p.finalPayPlanned || p.payStatus === 'FINAL_PAY_DONE')
        return null;
    return p.status === 'FINISHING' || p.status === 'INACTIVE' ? 'Final pay planned' : null;
}
/**
 * Spec §20a: *"Inactive = set inactive in RT Hub, or no active client placement in VA Roster
 * (resigned / ended)."* Onboarding while an onboarding case is open, or while they have been invited
 * and have not signed in yet (Ara, 13 Oct 2026: everyone in Users & Team is listed, and an invited
 * account is switched off in RT Hub until the invite is accepted — that is not someone who left). Finishing when every active
 * placement has an end date set. Internal team members have no placements, so for them only the
 * RT Hub switch decides.
 */
export function payrollPersonStatus(p) {
    if (!p.isActive)
        return p.invited ? 'ONBOARDING' : 'INACTIVE';
    if (p.onboardingOpen)
        return 'ONBOARDING';
    if (!p.isVa)
        return 'ACTIVE';
    if (p.activePlacements.length === 0)
        return 'INACTIVE';
    if (p.activePlacements.every((a) => a.endedAt != null))
        return 'FINISHING';
    return 'ACTIVE';
}
/**
 * Spec §20a: *"Search box on the People list: name, legal name, employee no., ANY of the person's
 * emails (work/RT Hub login, personal, Wise email) or client."* Case-insensitive; every word must
 * match somewhere, so a first name plus a client name finds that person on that client.
 */
export function matchesPeopleSearch(p, query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length)
        return true;
    const hay = [p.fullName, p.legalName, p.preferredName, p.employeeId, ...p.emails, ...p.activeClients]
        .filter((x) => !!x)
        .join(' ')
        .toLowerCase();
    return words.every((w) => hay.includes(w));
}
/** "Team member" for Internal Team Members, "Contractor" for everyone else (spec §1: employees / contractors). */
export function payrollPersonType(entityCode) {
    return entityCode === 'INTERNAL' ? 'Team member' : 'Contractor';
}
export const PERSON_STATUS_LABEL = { ACTIVE: 'Active', ONBOARDING: 'Onboarding', FINISHING: 'Finishing', INACTIVE: 'Inactive' };
/**
 * Spec §17: "change the hourly or weekend rate in Payroll (person → Pay & Wise → Change rate)". What
 * the form sends: only the rates that actually change, so an untouched weekend rate is never rewritten.
 * A blank hourly rate is refused (a rate is removed in Pay setup, on purpose); a blank weekend rate
 * means "regular rate" and clears it. Returns the problem, or the body to save (empty = nothing to do).
 */
export function rateChangeBody(now, typed) {
    const rateText = typed.payRate.trim();
    const weekendText = typed.weekendRate.trim();
    if (rateText === '')
        return { problem: 'Enter the hourly rate.' };
    const rate = Number(rateText);
    if (!(Number.isFinite(rate) && rate > 0))
        return { problem: 'The hourly rate must be above zero.' };
    const weekend = weekendText === '' ? null : Number(weekendText);
    if (weekend != null && !(Number.isFinite(weekend) && weekend > 0))
        return { problem: 'The weekend rate must be above zero — leave it blank to pay weekends at the regular rate.' };
    const body = {};
    if (rate !== now.payRate)
        body.payRate = rate;
    if (weekend !== now.weekendRate)
        body.weekendRate = weekend;
    return { body };
}
//# sourceMappingURL=payrollPeople.js.map